<template>
  <aside
    class="collapsible-aside"
    :class="{ 'collapsible-aside--collapsed': isCollapsed }"
    :style="asideStyle"
  >
    <div class="collapsible-aside__rail">
      <button
        class="collapsible-aside__rail-handle"
        type="button"
        :title="`${isCollapsed ? 'Show' : 'Hide'} ${panelName}`"
        @click="toggle"
      >
        <span
          v-if="isCollapsed"
          class="collapsible-aside__rail-text"
        >{{
          panelName
        }}</span>
        <i
          class="collapsible-aside__rail-icon"
          :class="
            isCollapsed
              ? 'el-icon-s-operation reverse'
              : 'el-icon-d-arrow-right'
          "
        />
      </button>
    </div>

    <div class="collapsible-aside__content">
      <slot />
    </div>
  </aside>
</template>

<script>
import VueTypes from 'vue-types'

export default {
  name: 'CollapsibleAside',

  props: {
    defaultCollapsed: VueTypes.bool.def(false),
    expandedWidth: VueTypes.string.def('256px'),
    collapsedWidth: VueTypes.string.def('40px'),
    panelName: VueTypes.string.isRequired
  },

  data () {
    return { isCollapsed: this.defaultCollapsed }
  },

  computed: {
    /**
     * @returns {object}
     */
    asideStyle () {
      const width = this.isCollapsed ? this.collapsedWidth : this.expandedWidth

      return {
        width,
        flexBasis: width,
        '--rail-width': this.collapsedWidth
      }
    }
  },

  methods: {
    /**
     *
     */
    toggle () {
      this.isCollapsed = !this.isCollapsed
    }
  }
}
</script>

<style lang="scss" scoped>
$aside-motion: 160ms cubic-bezier(0.2, 0.8, 0.2, 1);
$rail-motion: 120ms cubic-bezier(0.2, 0.8, 0.2, 1);

$rail-handle-min-width: spacing(7/2); // 28px if spacing(1) = 8px
$rail-handle-min-height: spacing(7); // 56px
$rail-handle-collapsed-height: spacing(17); // 136px
$rail-handle-expanded-width: spacing(4); // 32px
$rail-handle-expanded-height: spacing(6); // 48px
$rail-handle-radius: spacing(99);

$rail-label-font-size: 14px;
$rail-icon-font-size: spacing(2);

.collapsible-aside {
  position: relative;
  display: grid;
  flex: 0 0 auto;
  grid-template-columns: var(--rail-width, 40px) minmax(0, 1fr);
  min-width: var(--rail-width, 40px);
  height: 100%;
  overflow: hidden;
  background: transparent;
  // border-left: $border-divider;
  // box-shadow: $shadow-default;

  transition: width $aside-motion, flex-basis $aside-motion;
}

.collapsible-aside__rail {
  display: flex;
  grid-column: 1;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: transparent;
  pointer-events: none;
}

.collapsible-aside__rail-handle {
  display: inline-flex;
  gap: spacing(1);
  align-items: center;
  justify-content: center;
  min-width: $rail-handle-min-width;
  min-height: $rail-handle-min-height;
  padding: spacing(1);
  color: $blue;
  font-family: 'Euclid Flex', sans-serif;
  background: $blue-background;
  border: $border-divider;
  border-color: $blue-outline;
  border-radius: $rail-handle-radius;
  box-shadow: $shadow-default;
  cursor: pointer;
  transition:
    background-color $rail-motion,
    border-color $rail-motion,
    color $rail-motion,
    transform $rail-motion,
    box-shadow $rail-motion;
  pointer-events: auto;

  &:hover,
  &:focus-visible {
    color: $white;
    background-color: $blue;
    border-color: rgba(64, 158, 255, 0.35);
  }

  &:focus {
    outline: 0;
  }

  &:focus-visible {
    outline: spacing(1/4) solid rgba(64, 158, 255, 0.7);
    outline-offset: spacing(1/4);
  }
}

.collapsible-aside--collapsed .collapsible-aside__rail-handle {
  flex-direction: column;
  min-height: $rail-handle-collapsed-height;
}

.collapsible-aside:not(.collapsible-aside--collapsed) .collapsible-aside__rail-handle {
  width: $rail-handle-expanded-width;
  min-width: $rail-handle-expanded-width;
  height: $rail-handle-expanded-height;
  min-height: $rail-handle-expanded-height;
  padding: 0;
  border-radius: $rail-handle-radius 0 0 $rail-handle-radius;
  transform: translateX(spacing(1/8));
}

.collapsible-aside__rail-text {
  display: block;
  font-weight: $font-weight-bold;
  font-size: $rail-label-font-size;
  line-height: 1;
  letter-spacing: 0.18em;
  white-space: nowrap;
  transform: rotate(180deg);
  writing-mode: vertical-rl;
}

.collapsible-aside__rail-icon {
  font-size: $rail-icon-font-size;
  line-height: 1;
}

.reverse {
  transform: rotate(90deg);
}

.collapsible-aside__content {
  grid-column: 2;
  min-width: 0;
  height: 100%;
  overflow: hidden;
  box-shadow: $shadow-default;
  visibility: visible;
  opacity: 1;
  transition: opacity $rail-motion, visibility $rail-motion;
}

.collapsible-aside--collapsed .collapsible-aside__content {
  visibility: hidden;
  opacity: 0;
  pointer-events: none;
}
</style>
